import { useMemo } from "react";
import {
  buildCharacterList,
  buildFormatOptions,
  buildSigningTypes,
  buildVaList,
  extractCustomImageProducts,
  filterLiveEvents,
  filterNonLiveEvents,
  processProducts,
} from "../utils.js";

export function useShopProducts(data) {
  const isCollection = Boolean(data?.group_shop);
  const charactersMap = data?.characters;
  const talentMap = data?.talent;

  const allProducts = useMemo(
    () => processProducts(data, charactersMap),
    [data, charactersMap],
  );

  const formatOptions = useMemo(
    () => buildFormatOptions(allProducts),
    [allProducts],
  );

  const signingTypes = useMemo(
    () => buildSigningTypes(allProducts),
    [allProducts],
  );

  const characterList = useMemo(
    () => buildCharacterList(allProducts, charactersMap),
    [allProducts, charactersMap],
  );

  const vaList = useMemo(
    () => (isCollection ? buildVaList(allProducts, talentMap) : []),
    [isCollection, allProducts, talentMap],
  );

  const stats = useMemo(() => {
    if (!isCollection) return null;
    const prices = allProducts.map((p) => p._totalPrice);
    const hasPrices = prices.length > 0;
    return {
      vaCount: vaList.length,
      productCount: allProducts.length,
      priceMin: hasPrices ? Math.min(...prices) : null,
      priceMax: hasPrices ? Math.max(...prices) : null,
    };
  }, [isCollection, allProducts, vaList]);

  const customImageProducts = useMemo(
    () => extractCustomImageProducts(data, charactersMap),
    [data, charactersMap],
  );

  const events = useMemo(() => filterLiveEvents(data?.events || []), [data]);
  const nonLiveEvents = useMemo(
    () => filterNonLiveEvents(data?.events || [], true),
    [data],
  );

  const socials = useMemo(() => {
    if (!data || isCollection) return null;
    const entries = Object.entries({
      instagram: data.instagram_handle,
      twitter: data.twitter_handle,
      tiktok: data.tiktok_handle,
      youtube: data.youtube_handle,
    }).filter(([, val]) => val);
    return entries.length === 0 ? null : Object.fromEntries(entries);
  }, [data, isCollection]);

  return {
    allProducts,
    characterList,
    charactersMap,
    customImageProducts,
    events,
    formatOptions,
    nonLiveEvents,
    signingTypes,
    socials,
    stats,
    vaList,
  };
}
